﻿<script setup lang="ts">
    import { reactive, onMounted, inject, provide, watch } from 'vue';
    import { loadForm, Form, Theme } from './FormEditor.access';
    import { getHtml } from './FormEditor';
    import { type ToCopy } from './FormEditor.access';
    import { type ImageReference } from '@cwd/shared/components/Images/types';
    import Tabs, { tab } from '@shared/components/Tabs.vue';
    import content from './Content.vue';
    import contactlists from './ContactLists.vue';
    import formfields from './FormFields.vue';
    import formrender from './FormRender.vue';
    import settings from './Settings.vue';
    import design from './Design.vue';
    import Modal from '@shared/components/Modal.vue';
    import EditorContainer from '@shared/components/Editor/EditorContainer.vue';
    import ImageLibrary from '@shared/components/Images/ImageLibrary.vue';
    import { widgetRuleset } from '@views/Web/WidgetService';
    import { postWebContentChanges, WebContent, getContent } from '@views/Web/Forms.access';
    import { copyToClipboard } from '@views/Web/copyToClipboard';
    import { bsAlert } from '@shared/components/bsAlerts';
    import BSFormInputText from '@shared/components/BSFormInputText.vue';
    import BSFormInputTextarea from '@shared/components/BSFormInputTextarea.vue';
    import { injectorAccessInjectionKey } from '@cwd/lib/provider-keys';
    import { ContextAccess } from '@shared/Root/Context.access';
    import { useRouter } from 'vue-router';
    import { AccountAccess, UserAccountReference } from '@cwd/Account/Account.access';
    import {
        type LinkEditorProvide,
        linkEditorProvideName,
        type SwatchesEditorProvide,
        swatchesEditorProvideName,
    } from '@shared/components/RichEditor/Types.d';
    import { richEditorCleanup } from '@shared/components/RichEditor/utils';
    import LinkEditor from '@shared/components/RichEditor/LinkEditor.vue';
    import { setupLinkEditor } from '@shared/components/RichEditor/LinkEditor';
    import ColorPickerSwatches from '@shared/components/RichEditor/ColorPickerSwatches.vue';
    import { setupSwatches } from '@shared/components/RichEditor/ColorPickerSwatches';

    const router = useRouter();
    const injectorAccess = inject(injectorAccessInjectionKey)!;
    const contextAccess: ContextAccess = injectorAccess.injector.get(ContextAccess);
    const accountAccess: AccountAccess = injectorAccess.injector.get(AccountAccess);
    const currentAccount = accountAccess.state.currentAccount;

    const linkEditor = setupLinkEditor();
    provide<LinkEditorProvide>(linkEditorProvideName, {
        newLink: linkEditor.newLink,
        insertLink: linkEditor.insertLink,
        unsetLink: linkEditor.unsetLink,
        updateState: linkEditor.updateState,
        link: linkEditor.link,
    });

    const swatchesEditor = setupSwatches(currentAccount!.AccountId);
    provide<SwatchesEditorProvide>(swatchesEditorProvideName, {
        swatches: swatchesEditor.swatches,
        updateState: swatchesEditor.updateState,
        manageSwatches: swatchesEditor.manageSwatches,
        addSwatch: swatchesEditor.addSwatch,
        removeSwatch: swatchesEditor.removeSwatch,
        isValid: swatchesEditor.isValid,
    });

    const props = defineProps<{
        webContentId: string;
        copyFromWebContentId: string;
    }>();

    const ruleset = inject<widgetRuleset>('widgetRuleset');

    const state = reactive<{
        form: Form | null;
        html: string | null;
        plainHtml: string | null;
        js: string | null;
        viewHtml: boolean;
        showImageLibrary: boolean;
        currentImage: ImageReference | null;
        currentContent: WebContent;
        saving: boolean;
        toCopy: ToCopy;
    }>({
        form: null,
        html: null,
        plainHtml: null,
        js: null,
        viewHtml: false,
        showImageLibrary: false,
        currentImage: null,
        currentContent: { ContentType: 'Forms' } as WebContent,
        saving: false,
        toCopy: null,
    });

    const toggleImageLibrary = () => {
        state.showImageLibrary = !state.showImageLibrary;
    };

    const replaceImageSrc = (newValue: string = '') => {
        if (!state.currentImage) return;
        state.currentImage.src = newValue;
    };

    provide('toggleImageLibrary', toggleImageLibrary);

    const tempWebContentId = crypto.randomUUID();

    onMounted(async () => {
        initForm();

        watch(
            () => props.webContentId,
            (newVal) => {
                initForm();
            }
        );
    });

    const initForm = async () => {
        state.form = null; // reset

        if (props.webContentId || props.copyFromWebContentId) {
            let id = props.webContentId || props.copyFromWebContentId;
            let content = await getContent(id);

            state.currentContent = content[0];

            if (content[0].Data.cssVars === null) content[0].Data.cssVars = {}; // fix

            let form = content[0].Data;

            if (props.copyFromWebContentId) {
                content[0].WebContentId = '';
                content[0].Name = content[0].Name + ' [' + $Resources.CopyLabel + ']';
            }

            state.form = form;
        } else {
            // this it not how we should do it just testing to check how everything works
            state.currentContent = { ContentType: 'Forms' } as WebContent;

            let data = await loadForm(0);
            let privacyUrl = contextAccess.state.currentAccount!.PrivacyUrl;

            data.consent = data.consent.replace('#', privacyUrl ?? '#');
            state.form = data;
        }
    };

    const viewHtml = () => {
        let result = getHtml(props.webContentId || tempWebContentId, state.form as Form, contextAccess.state.currentAccount);
        if (!result) return;

        state.html = result.html;
        state.plainHtml = result.plainHtml ? result.plainHtml : '';
        state.js = result.js ? result.js : '';

        state.viewHtml = true;
    };

    const saveForm = async (): Promise<WebContent> => {
        state.saving = true;

        const content = state.currentContent;
        content.Data = state.form;
        content.Data.introduction = richEditorCleanup(content.Data.introduction);
        content.Data.consent = richEditorCleanup(content.Data.consent);

        content.Data.renderedHtml = getHtml(props.webContentId || tempWebContentId, state.form as Form, contextAccess.state.currentAccount).html;

        const savedContent = await postWebContentChanges(content);

        state.currentContent = savedContent;
        state.saving = false;

        bsAlert({ title: $Resources.FormSavedAlertHeading, content: $Resources.FormSavedAlertBody });

        if (!content.WebContentId) {
            router.replace({ path: '/Web/Forms/edit/' + savedContent.WebContentId });
        }

        return savedContent;
    };

    const updateCssVars = (name: string, newValue: string) => {
        if (!state.form) return;
        state.form.cssVars[name] = newValue;
    };

    const updateTheme = (theme: Theme) => {
        if (state.form === null) return;

        for (var key in theme.cssVars) {
            state.form.cssVars[key] = theme.cssVars[key];
        }

        state.form.googleFonts = theme.googleFonts;
    };

    const copyCode = () => {
        switch (state.toCopy) {
            case 'htmlCss':
                return copyToClipboard(state.html!);
            case 'js':
                return copyToClipboard('<script>' + state.js + '</' + 'script>');
            default:
                return copyToClipboard(state.plainHtml!);
        }
    };
</script>

<script lang="ts">
    export default { name: 'FormEditor' };
</script>

<template>
    <EditorContainer :show="state.form" :loading="!state.form" paneSide="right">
        <template #canvas>
            <formrender
                v-if="state.form"
                :id="props.webContentId || tempWebContentId"
                :publicHost="contextAccess.state.currentAccount!.PublicEventUrl.PublicHost"
                :currentAccountId="contextAccess.state.currentAccount!.AccountId"
                :form="state.form"></formrender>
        </template>
        <template #paneNav>
            <h2 class="h4 m-0-all strong c-light">{{ $Resources.FormEditorName }}</h2>
            <button type="button" class="btn btn-default m-auto-l" @click="viewHtml">{{ $Resources.FormEditorViewHtml }}</button>
            <button type="button" class="btn btn-primary" @click="saveForm" :class="{ 'is-busy': state.saving }">
                {{ $Resources.FormEditorSaveForm }}
            </button>
        </template>
        <template #pane>
            <BSFormInputText
                class="form-group"
                v-model="state.currentContent.Name"
                :label="$Resources.FormEditorFormNameLabel"
                :placeholder="$Resources.FormEditorFormNamePlaceholder"></BSFormInputText>

            <BSFormInputTextarea
                class="form-group"
                v-model="state.currentContent.Description"
                :label="$Resources.FormEditorFormDescriptionLabel"
                :placeholder="$Resources.FormEditorFormDescriptionPlaceholder"></BSFormInputTextarea>

            <tabs navClass="tabs list-unstyled d-flex flex-justified text-center" v-if="state.form">
                <tab :title="$Resources.FormEditorTabContent">
                    <content
                        v-model:title="state.form.title"
                        v-model:introduction="state.form.introduction"
                        v-model:consent="state.form.consent"
                        v-model:button="state.form.button"
                        :image="state.form.image"
                        @update:currentImage="(newValue: ImageReference) => (state.currentImage = newValue)"
                        @update:imageSrc="replaceImageSrc"></content>
                </tab>
                <tab :title="$Resources.FormEditorTabLists">
                    <contactlists
                        v-model:listsDisplay="state.form.listsDisplay"
                        :listsLegend="state.form.listsLegend"
                        :lists="state.form.lists"
                        @update:listLegend="(newVal) => (state.form!.listsLegend! = newVal)"></contactlists>
                </tab>
                <tab :title="$Resources.SubsciptionFormsSubscriberInputsHeading">
                    <formfields :formFields="state.form.formFields"></formfields>
                </tab>
                <tab :title="$Resources.FormEditorTabSettings">
                    <settings
                        v-model:confirmationIssueId="state.form.confirmationIssueId"
                        v-model:doubleOptInIssueId="state.form.doubleOptInIssueId"
                        v-model:doubleOptInUrl="state.form.doubleOptInUrl"
                        v-model:subscriptionConfirmedUrl="state.form.subscriptionConfirmedUrl"
                        v-model:subscriptionFailedUrl="state.form.subscriptionFailedUrl"
                        v-model:defaultCountryCode="state.form.defaultCountryCode"></settings>
                </tab>
                <tab :title="$Resources.FormEditorTabDesign">
                    <design
                        :cssVars="state.form.cssVars"
                        v-model:googleFonts="state.form.googleFonts"
                        @update:cssVars="updateCssVars"
                        @update:theme="updateTheme"></design>
                </tab>
            </tabs>
        </template>
    </EditorContainer>

    <div v-if="state.form">
        <Modal :title="$Resources.FormEditorViewHtml" size="lg" :scrollBody="true" :minHeight="true" :show="state.viewHtml" @close="state.viewHtml = false">
            <template #content>
                <tabs>
                    <tab title="Html" @tabClick="state.toCopy = 'plainHtml'">
                        <pre>{{ state.plainHtml }}</pre>
                        <input type="text" :value="state.plainHtml" id="plainHtmlToCopy" style="display: none" />
                    </tab>
                    <tab title="Html & CSS" @tabClick="state.toCopy = 'htmlCss'">
                        <pre>{{ state.html }}</pre>
                        <input type="text" :value="state.html" id="htmlToCopy" style="display: none" />
                    </tab>
                    <tab :title="$Resources.SubsciptionFormsJSHeading" @tabClick="state.toCopy = 'js'">
                        <pre>{{ '<script>' + state.js + '</script>' }}</pre>
                        <input type="text" :value="state.js" id="jsToCopy" style="display: none" />
                    </tab>
                </tabs>
            </template>
            <template #button>
                <button type="button" class="btn btn-primary" @click="copyCode">
                    {{ $Resources.CopyToClipBoardBtn }}
                </button>
            </template>
        </Modal>

        <Modal :title="$Resources.ImageDialogHeading" size="fullscreenBlank" :show="state.showImageLibrary" @close="toggleImageLibrary">
            <template #content>
                <ImageLibrary :init="state.showImageLibrary" :image="state.currentImage" @update:imageSrc="replaceImageSrc"></ImageLibrary>
            </template>
        </Modal>
    </div>

    <LinkEditor></LinkEditor>

    <ColorPickerSwatches></ColorPickerSwatches>
</template>

<style lang="less">
    .list-enter-active,
    .list-leave-active {
        transition:
            opacity 300ms ease-in-out,
            transform 300ms ease-in-out,
            background-color 300ms ease-in-out !important;
    }

    .list-enter-from,
    .list-leave-to {
        opacity: 0 !important;
        transform: scaleY(0);
        transform: scale(0.9) !important;
        transform-origin: center !important;
    }

    .list-enter-from {
        background-color: #1fa9b0 !important;
    }

    .list-leave-to {
        background-color: #999 !important;
    }

    .tabs.flex-justified > li > a {
        padding-left: 0;
        padding-right: 0;
    }

    .modal-scroll-body .modal-body {
        min-height: 70vh;
    }
</style>

<style scoped lang="less">
    pre {
        white-space: normal;
    }
</style>
